import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import {
  Alert, Box, CreateHeaders, dateHeader, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum,
  Link, List, ListItem, ListItemIcon, ListItemText, PrimaryButton, Tab, Tabs, textHeader, Tooltip, Typography
} from '@frontend-workspace/react-shared-lib';
import { IOfferContractsWithProduct, IOfferContractsWithProductSeats } from '../../_models/offer-contracts-with-product';
import { getDateAfterMonths, getPlainDateWithoutHooks } from '../../helpers';
import WebkuaTable from '../../webkua-table/webkua-table';
import { ContractDetailsDialog, useSalesOfferContext } from '@frontend-workspace/webkua-lib';

/* eslint-disable-next-line */
export interface SalesOfferContractSelectionProps {
  open: boolean,
  onClose: () => void,
  onComplete: () => void,
  contracts?: IOfferContractsWithProduct | null
}

export function SalesOfferContractSelection(props: SalesOfferContractSelectionProps) {
  const { t } = useTranslation();
  const { formData, updateFormData } = useSalesOfferContext();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [openContractDetails, setOpenContractDetails] = useState(false);
  const [contractDetail, setContractDetail] = useState<IOfferContractsWithProductSeats>();

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);

  useEffect(() => {
    if (props.contracts?.contractsWithProduct.length) {
      setSelectedTab(0);
    } else if (props.contracts?.contractsWithoutProduct.length) {
      setSelectedTab(1);
    } else {
      setSelectedTab(2);
    }
  }, [props.contracts]);

  const selectContract = (contract: IOfferContractsWithProductSeats) => {
    updateFormData('contract', contract);
    props.onComplete();
  };

  const selectNewContract = () => {
    const newContract: IOfferContractsWithProductSeats = {
      contractNumber: 'Ny',
      seats: 0,
      renewalDate: getDateAfterMonths(12),
      invoiceInterval: 12,
      contractResponsibleName: 'TODOfirstname' + ' ' + 'TODOLastname',
      licenses: []
    }
    updateFormData('contract', newContract);
    props.onComplete();
  };

  const contractDetailsCellRenderer = (props: any) => (
    <Link underline="always" onClick={(e) => { e.preventDefault(); props.onClick(props.data) }}>
      {props.value}
    </Link>
  );

  const addProductButtonCellRenderer = (props: any) => (
    <Tooltip
      arrow
      title={t('shop.invoiceIntervalInfoText')}
      disableHoverListener={props.data.invoiceInterval === 12}>
      <PrimaryButton
        variant={props.variant || "outlined"}
        size='small'
        disabled={props.data.invoiceInterval !== 12}
        onClick={() => { props.onClick(props.data) }}>
        {props.buttonText}
      </PrimaryButton>
    </Tooltip>
  );

  const showContractDetails = (contract: IOfferContractsWithProductSeats) => {
    setOpenContractDetails(true);
    setContractDetail(contract);
  };

  const hideContractDetails = () => {
    setOpenContractDetails(false);
    setContractDetail(undefined);
  };

  interface TableOfContractsProps {
    data: IOfferContractsWithProductSeats[]
  }
  const TableOfContracts = (props: TableOfContractsProps) => {
    return (
      <WebkuaTable
        headers={CreateHeaders([
          textHeader("contractNumber", t("shop.contractNumber"), HeaderSizeEnum.MEDIUM, {
            renderer: contractDetailsCellRenderer,
            rendererParams: {
              onClick: (e: IOfferContractsWithProductSeats) => {
                showContractDetails(e);
              }
            }
          }),
          textHeader("seats", t("shop.currentSeats"), HeaderSizeEnum.MEDIUM),
          dateHeader("renewalDate", t("shop.renewalDate")),
          textHeader("invoiceInterval", t("shop.invoiceInterval"), HeaderSizeEnum.MEDIUM),
          textHeader("removeLicense", "", HeaderSizeEnum.SMALL, {
            renderer: addProductButtonCellRenderer,
            rendererParams: {
              buttonText: t("shop.add"),
              onClick: (e: IOfferContractsWithProductSeats) => {
                selectContract(e);
              }
            },
            cellStyle: { justifyContent: 'right' }
          })
        ])}
        data={props.data || []}
        pageSize={10}
      />)
  };

  const NewLicense = () => {
    return <>
      <Alert severity="info" className="mb-5">
        {t("shop.newLicenseMsg")}
      </Alert>
      <Row>
        <Col sm={6} className="offset-sm-3 mb-2">
          <PrimaryButton
            variant="contained"
            onClick={selectNewContract}
            fullWidth>
            {t("shop.addNewContract")}
          </PrimaryButton>
        </Col>
      </Row>
      <div className="d-flex justify-content-center mb-5">
        {t("shop.renewalDate")}: {getPlainDateWithoutHooks(getDateAfterMonths(12))}
      </div>
    </>
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth={true}>
      <DialogTitle className='mb-4'>
        {t("shop.addNewOrExisting")}
      </DialogTitle>
      <DialogContent sx={{ height: '930px' }}>
        <Box>
          {props.contracts && <>
            <List className='mb-3' dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon>
                  <img src={props.contracts.iconUrl} alt="" width={40} height={40} className='me-3' />
                </ListItemIcon>
                <ListItemText primary={<span className='MuiTypography-body2'>{props.contracts.productName}</span>} />
              </ListItem>
            </List>
            <div className="d-flex mb-5">
              <Typography
                component="div"
                variant="subtitle4"
                className='me-1'>
                {t("shop.requires")}:
              </Typography>
              <Typography
                component="div"
                variant="body6">
                {props.contracts.requirements}
              </Typography>
            </div>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
              <Tabs
                value={selectedTab}
                variant="scrollable"
                scrollButtons="auto">
                <Tab disabled={props.contracts.contractsWithProduct.length === 0} label={t("shop.addSeats")} onClick={navigateToTabOne} />
                <Tab disabled={props.contracts.contractsWithoutProduct.length === 0} label={t("shop.addLicense")} onClick={navigateToTabTwo} />
                <Tab label={t("shop.newContract")} onClick={navigateToTabThree} />
              </Tabs>
            </Box>
            {selectedTab === 0 && <>
              <Alert severity="info" className="mb-5">
                {t("shop.addSeatsMsg")}
              </Alert>
              <TableOfContracts data={props.contracts.contractsWithProduct}></TableOfContracts>
            </>
            }
            {selectedTab === 1 && <>
              <Alert severity="info" className="mb-5">
                {t("shop.addLicenseMsg")}
              </Alert>
              <TableOfContracts data={props.contracts.contractsWithoutProduct}></TableOfContracts>
            </>
            }
            {selectedTab === 2 && <NewLicense />}
            {contractDetail &&
              <ContractDetailsDialog
                open={openContractDetails}
                onClose={() => hideContractDetails()}
                contract={contractDetail}
                onConfirm={() => selectContract(contractDetail)}
                onCancel={() => hideContractDetails()}
              />
            }
          </>}
        </Box>
      </DialogContent >
      <DialogActions>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog >
  );
}

export default SalesOfferContractSelection;
